import type { Level } from '../core/hp/types';
import { stackedItems } from '../core/geometry/stacking';
import type { SurfaceProbe } from '../core/geometry/landing';
import type { Selection } from './store';
import { isPlanItemVisible, type PlanSurface } from './planVisibility';

/**
 * What a tap should select.
 *
 * Tapping the object that is already selected clears the selection — the native app's
 * rule — which is how the context bar is dismissed without hunting for empty space.
 * A wall counts as the same object whichever face was tapped.
 */
export function nextSelectionOnTap(current: Selection, tapped: Selection): Selection {
  if (!current || !tapped) return tapped;
  if (current.kind !== tapped.kind) return tapped;
  switch (tapped.kind) {
    case 'item':
    case 'opening':
    case 'wall':
      return (current as { id: string }).id === tapped.id ? null : tapped;
    case 'room':
      return (current as { index: number }).index === tapped.index ? null : tapped;
    case 'trim':
      return (current as { wallId: string; trim: string }).wallId === tapped.wallId
        && (current as { wallId: string; trim: string }).trim === tapped.trim ? null : tapped;
    case 'vertex':
      return (current as { wallId: string; end: string }).wallId === tapped.wallId
        && (current as { wallId: string; end: string }).end === tapped.end ? null : tapped;
    default:
      return tapped;
  }
}

/** The items a selection holds: one for a single item, several for a Shift-gathered group, none otherwise. */
export function selectedItemIds(selection: Selection): string[] {
  return selection?.kind === 'item' ? [selection.id] : selection?.kind === 'items' ? selection.ids : [];
}

/**
 * What a Shift-tap on an item selects: the item joins the items already selected, or leaves
 * them if it was one of them. A group of one is a plain item selection again, and anything
 * else selected (a wall, a room) gives way to the item.
 */
export function toggleItemSelection(current: Selection, id: string): Selection {
  const ids = selectedItemIds(current);
  const next = ids.includes(id) ? ids.filter(value => value !== id) : [...ids, id];
  return next.length > 1 ? { kind: 'items', ids: next } : next.length ? { kind: 'item', id: next[0] } : null;
}

/**
 * ⌘A: every item of the floor a plan surface shows, as the group a Shift-gather would make of them; in 3D (`surface`
 * left out) every item. The wall plan picks nothing.
 */
export function everyItem(level: Pick<Level, 'items' | 'height'> | undefined, surface?: PlanSurface | 'wall'): Selection {
  if (surface === 'wall') return null;
  const ids = (level?.items ?? []).filter(item => !surface || isPlanItemVisible(item, surface, level?.height)).map(item => item.id);
  return ids.length > 1 ? { kind: 'items', ids } : ids.length ? { kind: 'item', id: ids[0] } : null;
}

/**
 * What a context press selects: the object under it, never toggled off; an item of the gathered group keeps the group.
 * A room's floor is the canvas, where «Вставить сюда» lands, unless that room is already selected.
 */
export function contextSelection(current: Selection, pressed: Selection): Selection {
  if (pressed?.kind === 'item' && current?.kind === 'items' && current.ids.includes(pressed.id)) return current;
  if (pressed?.kind === 'room') return current?.kind === 'room' && current.index === pressed.index ? current : null;
  return pressed;
}

/**
 * The picked pieces a move or a delete takes: the unlocked ones, and how many locked ones stay where they are. A locked
 * piece standing on a picked support goes with it, so it is not counted as staying.
 */
export function movableItems(level: Pick<Level, 'items'>, ids: readonly string[], probe?: SurfaceProbe): { ids: string[]; held: number } {
  const locked = new Set(level.items.filter(item => item.locked && ids.includes(item.id)).map(item => item.id));
  const moving = ids.filter(id => !locked.has(id)), carried = new Set<string>();
  for (const base of level.items) if (moving.includes(base.id)) for (const rider of stackedItems(level.items, base, probe)) carried.add(rider.id);
  return { ids: moving, held: [...locked].filter(id => !carried.has(id)).length };
}
